import { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";

// Generic action type constants (optional placeholders if real auth slice exists)
// Replace or integrate with existing auth slice actions if available.
const AUTH_LOGOUT = "auth/LOGOUT_PLACEHOLDER";

function safeParse(json) {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function getStoredUser() {
  if (typeof window === "undefined") return null;
  // Try both legacy and context keys
  const rawUser = localStorage.getItem("user");
  const rawCurrent = localStorage.getItem("currentUser");
  const user = rawUser
    ? safeParse(rawUser)
    : rawCurrent
      ? safeParse(rawCurrent)
      : null;
  if (user) return user;
  const id = localStorage.getItem("userId");
  return id ? { id } : null;
}

export default function useAuth() {
  const dispatch = useDispatch();
  // Broaden selector: support auth slice OR user slice currentUser.
  const sliceUser = useSelector((state) => {
    if (state.auth?.user) return state.auth.user;
    if (state.auth?.currentUser) return state.auth.currentUser;
    if (state.user?.currentUser) return state.user.currentUser; // from user slice actions
    return null;
  });

  const [user, setUserState] = useState(() => sliceUser || getStoredUser());

  // Sync with redux slice user
  useEffect(() => {
    if (sliceUser) {
      setUserState(sliceUser);
      if (typeof window !== "undefined") {
        try {
          localStorage.setItem("user", JSON.stringify(sliceUser));
          localStorage.setItem("currentUser", JSON.stringify(sliceUser)); // keep both in sync
          if (sliceUser.id)
            localStorage.setItem("userId", String(sliceUser.id));
        } catch {}
      }
    } else {
      // When redux currentUser becomes null, clear local state and storage
      setUserState(null);
      if (typeof window !== "undefined") {
        try {
          localStorage.removeItem("user");
          localStorage.removeItem("currentUser");
          localStorage.removeItem("userId");
        } catch {}
      }
    }
  }, [sliceUser]);

  const setUser = useCallback((u) => {
    setUserState(u);
    if (typeof window !== "undefined") {
      if (u) {
        try {
          localStorage.setItem("user", JSON.stringify(u));
          localStorage.setItem("currentUser", JSON.stringify(u));
        } catch {}
        if (u.id)
          try {
            localStorage.setItem("userId", String(u.id));
          } catch {}
      } else {
        try {
          localStorage.removeItem("user");
          localStorage.removeItem("currentUser");
          localStorage.removeItem("userId");
          localStorage.removeItem("accessToken");
          localStorage.removeItem("authToken");
        } catch {}
      }
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    if (typeof window !== "undefined") {
      try {
        localStorage.removeItem("user");
        localStorage.removeItem("currentUser");
        localStorage.removeItem("userId");
        localStorage.removeItem("token");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("authToken");
      } catch {}
    }
    try {
      dispatch({ type: AUTH_LOGOUT });
    } catch {}
  }, [dispatch, setUser]);

  return {
    user,
    isAuthenticated: !!user,
    setUser,
    logout,
  };
}
